import React from 'react'
import { cn } from '@/lib/utils'
import { CheckIcon, MinusIcon } from './Icons'

export interface CheckboxProps {
  /** Checkbox label */
  label?: string
  /** Checked state */
  checked: boolean
  /** Indeterminate state */
  indeterminate?: boolean
  /** Change handler */
  onChange: (checked: boolean) => void
  /** Help text */
  helpText?: string
  /** Error state */
  error?: string | boolean
  /** Disabled state */
  disabled?: boolean
  /** Input name */
  name?: string
  /** Input ID */
  id?: string
  /** Additional class name */
  className?: string
  /** Data ID for testing */
  'data-id'?: string
}

export const Checkbox: React.FC<CheckboxProps> = ({
  label,
  checked,
  indeterminate = false,
  onChange,
  helpText,
  error,
  disabled = false,
  name,
  id,
  className,
  'data-id': dataId,
}) => {
  const handleChange = () => {
    if (!disabled) {
      onChange(!checked)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      handleChange()
    }
  }

  const isChecked = checked || indeterminate

  return (
    <div className={cn('flex items-start gap-3', className)} data-id={dataId}>
      <div
        role="checkbox"
        aria-checked={indeterminate ? 'mixed' : checked}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        id={id}
        onClick={handleChange}
        onKeyDown={handleKeyDown}
        className={cn(
          'w-[18px] h-[18px] rounded flex items-center justify-center transition-all duration-150 cursor-pointer shrink-0 mt-0.5',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          disabled && 'cursor-not-allowed opacity-60',
          isChecked
            ? 'bg-violet-600 border border-violet-600'
            : 'bg-background border border-muted-foreground',
          error && !isChecked && 'border-destructive'
        )}
      >
        {indeterminate ? (
          <MinusIcon className="w-3 h-3 text-white" />
        ) : checked ? (
          <CheckIcon className="w-3 h-3 text-white" />
        ) : null}
      </div>
      {(label || helpText || error) && (
        <div className="flex flex-col gap-0.5">
          {label && (
            <label
              htmlFor={id}
              className={cn(
                'text-[14px] leading-5 cursor-pointer select-none',
                disabled ? 'text-muted-foreground' : 'text-foreground'
              )}
              onClick={handleChange}
            >
              {label}
            </label>
          )}
          {helpText && !error && (
            <span className="text-[12px] text-muted-foreground">{helpText}</span>
          )}
          {error && typeof error === 'string' && (
            <span className="text-[12px] text-destructive">{error}</span>
          )}
        </div>
      )}
      {name && (
        <input
          type="hidden"
          name={name}
          value={checked ? 'true' : 'false'}
        />
      )}
    </div>
  )
}
